import { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { EntityFormDialog } from '@/shared/components/EntityFormDialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { Separator } from '@/components/ui/separator';
import { ModalErrorBoundary } from '@/components/ErrorBoundary';
import { useHasRole } from '@/hooks/useSession';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { useIsSuperAdmin } from '../hooks/useIsSuperAdmin';
import { useHasTransmitterPermission } from '../hooks/useTransmitterPermission';
import { useTransmitterPopulate } from '../hooks/useTransmitterPopulate';
import { useTransmitterOptions } from '../hooks/useTransmitterOptions';
import { useUpdateTransmitter } from '../hooks/useUpdateTransmitter';
import { useHardwareOptions } from '../hooks/useHardwareOptions';
import { deleteTransmitter, resetSim } from '../api';
import { TRANSMITTER_PERMISSIONS } from '../permissions';
import { transmittersKeys } from '../hooks/useTransmitters';
import { OrderSpecsSection } from './sections/OrderSpecsSection';
import { ConfigDataSection } from './sections/ConfigDataSection';
import { ConnectivitySection } from './sections/ConnectivitySection';
import { StatusSection } from './sections/StatusSection';
import { DetailsSection } from './sections/DetailsSection';
import { FirmwareOtaSection } from './sections/FirmwareOtaSection';
import { ConfigsOtaSection } from './sections/ConfigsOtaSection';
import { ImageUploadSection } from './sections/ImageUploadSection';
import { DiagnosticsTab } from './tabs/DiagnosticsTab';
import { ChangelogTab } from './tabs/ChangelogTab';
import type {
  TransmitterFormData,
  TransmitterV2Document,
  TransmitterOptions,
} from '../types';

// =============================================================================
// Form Initialization & Diffing
// =============================================================================

/** Check if a GPS coordinate value is a valid number (not null, undefined, '--', or NaN) */
function isValidCoord(v: unknown): v is number {
  return v !== null && v !== undefined && v !== '--' && !Number.isNaN(Number(v));
}

function initFormData(t: TransmitterV2Document): TransmitterFormData {
  const gps = t.gps;
  const lat = isValidCoord(gps?.[0]) ? Number(gps![0]) : null;
  const lng = isValidCoord(gps?.[1]) ? Number(gps![1]) : null;

  // Hardware is stored in hardware_specs[{k:'base'|'module'|'connectivity', i:'0x10'}].
  // Each i is a hex string with '0x' prefix (e.g. '0x04', '0x10', '0x20').
  const hwSpecs = t.hardware_specs ?? [];
  const hwBase = hwSpecs.find((hw) => hw.k === 'base');
  const hwModule = hwSpecs.find((hw) => hw.k === 'module');
  const hwConn = hwSpecs.find((hw) => hw.k === 'connectivity');

  return {
    name: t.name || '',
    activated: t.activated ?? false,
    organisation: t.organisation || null,
    sales_order: t.sales_order || '',
    quote_number: t.quote_number || '',
    ticket_number: t.ticket_number || '',
    installed_at: t.installed_at || null,
    usergroup: t.usergroup || null,
    site: t.site || null,
    location: t.location || null,
    gps: [lat, lng],
    remote_config_enabled: t.remote_config_enabled ?? false,
    local_alerts_enabled: t.local_alerts_enabled ?? false,
    rssi_stream: t.rssi_stream ?? false,
    dc1_stream: t.dc1_stream ?? false,
    dc2_stream: t.dc2_stream ?? false,
    bv_stream: t.bv_stream ?? false,
    hardware_base: hwBase?.i || null,
    hardware_module: hwModule?.i || null,
    hardware_radio: hwConn?.i || null,
    key: t.key || null,
    xbee_SL: t.xbee_SL || '',
    simprovider: t.simprovider || '',
    ICCID: t.ICCID || '',
    IMEI: t.IMEI || '',
    description: t.description || '',
  };
}

function diffFormData(
  original: TransmitterFormData,
  current: TransmitterFormData,
): Partial<TransmitterFormData> {
  const changes: Partial<TransmitterFormData> = {};
  for (const key of Object.keys(current) as (keyof TransmitterFormData)[]) {
    if (JSON.stringify(original[key]) !== JSON.stringify(current[key])) {
      (changes as Record<string, unknown>)[key] = current[key];
    }
  }
  return changes;
}

// =============================================================================
// Loading Skeleton
// =============================================================================

function ModalSkeleton() {
  return (
    <div className="space-y-6 py-4">
      {Array.from({ length: 8 }).map((_, i) => (
        <div key={i} className="grid grid-cols-[140px_1fr] gap-2">
          <Skeleton className="h-4 w-24" />
          <Skeleton className="h-8 w-full" />
        </div>
      ))}
    </div>
  );
}

// =============================================================================
// Modal Component
// =============================================================================

interface TransmitterEditModalProps {
  transmitterId: string | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  deviceType?: 'transmitters' | 'gateways';
  /** Override the organisation shown in the modal (MongoDB org ID). When provided, this takes precedence over the MongoDB document's organisation field. */
  organisationId?: string | null;
}

export function TransmitterEditModal({
  transmitterId,
  open,
  onOpenChange,
  deviceType = 'transmitters',
  organisationId,
}: TransmitterEditModalProps) {
  const isAdmin = useHasRole('full_admin');
  const isSuperAdmin = useIsSuperAdmin();
  const canEditTransmitters = useHasTransmitterPermission(TRANSMITTER_PERMISSIONS.edit);
  const canDeleteTransmitters = useHasTransmitterPermission(TRANSMITTER_PERMISSIONS.delete);
  const canResetSim = useHasTransmitterPermission(TRANSMITTER_PERMISSIONS.resetSim);
  const canPushFirmware = useHasTransmitterPermission(TRANSMITTER_PERMISSIONS.pushFirmware);
  const canPushConfigs = useHasTransmitterPermission(TRANSMITTER_PERMISSIONS.pushConfigs);
  const canManageImages = useHasTransmitterPermission(TRANSMITTER_PERMISSIONS.manageImages);
  const canViewDiagnostics = useHasTransmitterPermission(TRANSMITTER_PERMISSIONS.diagnostics);
  const queryClient = useQueryClient();
  const [activeTab, setActiveTab] = useState('edit');

  // Data fetching
  const populateQuery = useTransmitterPopulate(open ? transmitterId : null, deviceType);
  const hardwareBaseQuery = useHardwareOptions('base');
  const hardwareModuleQuery = useHardwareOptions('module');
  const hardwareRadioQuery = useHardwareOptions('connectivity');

  // Form state
  const [formData, setFormData] = useState<TransmitterFormData | null>(null);
  const [options, setOptions] = useState<TransmitterOptions | null>(null);
  const formDataRef = useRef<TransmitterFormData | null>(null);
  const originalRef = useRef<TransmitterFormData | null>(null);
  const initializedTransmitterIdRef = useRef<string | null>(null);

  // Org change options reload
  const [orgChangeId, setOrgChangeId] = useState<string | null>(null);
  const optionsQuery = useTransmitterOptions(orgChangeId, deviceType);

  // Mutation
  const updateMutation = useUpdateTransmitter(transmitterId || '');

  // Delete/Reset SIM confirmation dialogs
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [showResetSimDialog, setShowResetSimDialog] = useState(false);

  useEffect(() => {
    formDataRef.current = formData;
  }, [formData]);

  // Delete mutation
  const deleteMutation = useMutation({
    mutationFn: () => deleteTransmitter(transmitterId!),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: transmittersKeys.all });
      toast.success('Transmitter deleted');
      onOpenChange(false);
    },
    onError: (error) => {
      toast.error('Failed to delete transmitter', {
        description: error instanceof Error ? error.message : 'Unknown error',
      });
    },
  });

  // Reset SIM mutation
  const resetSimMutation = useMutation({
    mutationFn: (iccid: string) => resetSim(iccid),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: transmittersKeys.all });
      toast.success('SIM reset initiated');
    },
    onError: (error) => {
      toast.error('Failed to reset SIM', {
        description: error instanceof Error ? error.message : 'Unknown error',
      });
    },
  });

  // Reset all transient state when modal closes, so stale data never flashes on reopen
  useEffect(() => {
    if (!open) {
      setFormData(null);
      setOptions(null);
      setOrgChangeId(null);
      setActiveTab('edit');
      setShowDeleteDialog(false);
      setShowResetSimDialog(false);
      formDataRef.current = null;
      originalRef.current = null;
      initializedTransmitterIdRef.current = null;
    }
  }, [open]);

  // Initialize form when populate data arrives
  useEffect(() => {
    if (populateQuery.data) {
      const incomingTransmitter = populateQuery.data.transmitter;
      const initial = initFormData(incomingTransmitter);
      // If an organisationId override was provided (e.g. from account dashboard where PG is source of truth),
      // use it instead of the MongoDB document's organisation field.
      if (organisationId) {
        initial.organisation = organisationId;
      }
      const isNewTransmitter = initializedTransmitterIdRef.current !== incomingTransmitter._id;
      const currentFormData = formDataRef.current;
      const hasDirtyForm = Boolean(
        currentFormData &&
        originalRef.current &&
        Object.keys(diffFormData(originalRef.current, currentFormData)).length > 0,
      );

      if (isNewTransmitter || !currentFormData || !originalRef.current || !hasDirtyForm) {
        setFormData(initial);
        formDataRef.current = initial;
        originalRef.current = initial;
        initializedTransmitterIdRef.current = incomingTransmitter._id;
      }

      setOptions(populateQuery.data.options);
      if (isNewTransmitter || !hasDirtyForm) {
        setOrgChangeId(organisationId && organisationId !== incomingTransmitter.organisation ? organisationId : null);
      }
    }
  }, [populateQuery.data, organisationId]);

  // Update options when org change response arrives
  useEffect(() => {
    if (optionsQuery.data?.options) {
      setOptions(optionsQuery.data.options);
    }
  }, [optionsQuery.data]);

  // Field updater
  const setField = useCallback(
    <K extends keyof TransmitterFormData>(key: K, value: TransmitterFormData[K]) => {
      setFormData((prev) => {
        if (!prev) return prev;
        const next = { ...prev, [key]: value };

        // When org changes, clear dependent fields
        if (key === 'organisation' && value !== prev.organisation) {
          next.site = null;
          next.location = null;
          next.key = null;
          next.usergroup = null;
        }

        return next;
      });

      // Trigger options reload outside the updater (safe to call setState from setState,
      // but NOT from inside a setState updater function in React 18 concurrent mode)
      if (key === 'organisation' && typeof value === 'string' && value) {
        setOrgChangeId(value);
      }
    },
    [],
  );

  // Dirty check
  const isDirty = useMemo(() => {
    if (!formData || !originalRef.current) return false;
    return Object.keys(diffFormData(originalRef.current, formData)).length > 0;
  }, [formData]);

  // Save handler
  const handleSave = async () => {
    if (!canEditTransmitters) {
      toast.error('You do not have permission to edit transmitters.');
      return;
    }
    if (!formData || !originalRef.current || !transmitterId) return;
    const changes = diffFormData(originalRef.current, formData);
    if (Object.keys(changes).length === 0) {
      onOpenChange(false);
      return;
    }

    // Transform hardware_base/module/radio fields into a hardware_specs array.
    // The V2 PUT endpoint expects hardware_specs: [{k, i}] — not individual scalar fields.
    const hardwareChanged =
      'hardware_base' in changes ||
      'hardware_module' in changes ||
      'hardware_radio' in changes;
    if (hardwareChanged) {
      const hwSpecs: Array<{ k: string; i: string }> = [];
      if (formData.hardware_base)
        hwSpecs.push({ k: 'base', i: formData.hardware_base });
      if (formData.hardware_module)
        hwSpecs.push({ k: 'module', i: formData.hardware_module });
      if (formData.hardware_radio)
        hwSpecs.push({ k: 'connectivity', i: formData.hardware_radio });
      delete changes.hardware_base;
      delete changes.hardware_module;
      delete changes.hardware_radio;
      (changes as Record<string, unknown>).hardware_specs = hwSpecs;
    }

    try {
      await updateMutation.mutateAsync(changes);
      onOpenChange(false);
    } catch {
      // Error already toasted by mutation
    }
  };

  // Discard-confirmation is now handled by EntityFormDialog (`confirmDiscardOnClose`).
  // Legacy showUnsavedDialog state is kept only for the local AlertDialog below
  // (no-op while EntityFormDialog owns the close flow).

  const transmitter = populateQuery.data?.transmitter;
  const isLoading = populateQuery.isLoading;
  const isError = populateQuery.isError;

  return (
    <>
      <EntityFormDialog
        open={open}
        onOpenChange={onOpenChange}
        size="3xl"
        title={`Edit: Transmitter — ${transmitter?.name || transmitter?.dev_id || '...'}`}
        isDirty={isDirty}
        onSubmit={handleSave}
        isSubmitting={updateMutation.isPending}
        saveDisabled={!isDirty}
        saveLabel={updateMutation.isPending ? 'Saving...' : 'Save'}
        confirmDiscardOnClose
        footerExtras={
          (isSuperAdmin && canDeleteTransmitters && transmitter) || (isSuperAdmin && canResetSim && transmitter?.ICCID) ? (
            <>
              {isSuperAdmin && canDeleteTransmitters && transmitter && (
                <Button
                  type="button"
                  variant="destructive"
                  onClick={() => setShowDeleteDialog(true)}
                  disabled={deleteMutation.isPending}
                >
                  {deleteMutation.isPending ? 'Deleting...' : 'Delete'}
                </Button>
              )}
              {isSuperAdmin && canResetSim && transmitter?.ICCID && (
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setShowResetSimDialog(true)}
                  disabled={resetSimMutation.isPending}
                >
                  {resetSimMutation.isPending ? 'Resetting...' : 'Reset SIM'}
                </Button>
              )}
            </>
          ) : undefined
        }
      >
          <ModalErrorBoundary>
          {isLoading && <ModalSkeleton />}

          {isError && (
            <div className="py-8 text-center">
              <p className="text-red-500">Failed to load transmitter data.</p>
              <Button
                type="button"
                variant="outline"
                className="mt-4"
                onClick={() => populateQuery.refetch()}
              >
                Retry
              </Button>
            </div>
          )}

          {transmitter && formData && options && (
            <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
              <TabsList className="w-full">
                <TabsTrigger value="edit" className="flex-1">Edit</TabsTrigger>
                {canViewDiagnostics && (
                  <TabsTrigger value="diagnostics" className="flex-1">Diagnostics</TabsTrigger>
                )}
                <TabsTrigger value="history" className="flex-1">History</TabsTrigger>
              </TabsList>

              <TabsContent value="edit" className="space-y-6 py-2">
                {/* Order Specifications — admin only */}
                {isAdmin && (
                  <>
                    <OrderSpecsSection
                      formData={formData}
                      setField={setField}
                      options={options}
                    />
                    <Separator />
                  </>
                )}

                {/* Configuration Data */}
                <ConfigDataSection
                  formData={formData}
                  setField={setField}
                  options={options}
                  transmitter={transmitter}
                  isAdmin={isAdmin}
                  hardwareBase={hardwareBaseQuery.data || []}
                  hardwareModule={hardwareModuleQuery.data || []}
                  hardwareRadio={hardwareRadioQuery.data || []}
                  onRefreshOptions={() => populateQuery.refetch()}
                  canCreateLocation={canEditTransmitters}
                />
                <Separator />

                {/* Firmware OTA — super admin only */}
                {isSuperAdmin && canPushFirmware && (
                  <>
                    <FirmwareOtaSection
                      transmitter={transmitter}
                      firmwares={'firmwares' in options ? options.firmwares : []}
                      onRefresh={() => populateQuery.refetch()}
                    />
                    <Separator />
                  </>
                )}

                {/* Configs OTA — admin only */}
                {canPushConfigs && (
                  <>
                    <ConfigsOtaSection
                      transmitter={transmitter}
                      onRefresh={() => populateQuery.refetch()}
                    />
                    <Separator />
                  </>
                )}

                {/* Connectivity Specifications — admin only */}
                {isAdmin && (
                  <>
                    <ConnectivitySection
                      formData={formData}
                      setField={setField}
                      options={options}
                    />
                    <Separator />
                  </>
                )}

                {/* Status (read-only) */}
                <StatusSection transmitter={transmitter} />
                <Separator />

                {/* Image Upload */}
                <ImageUploadSection
                  transmitter={transmitter}
                  onRefresh={() => populateQuery.refetch()}
                  canManageImages={canManageImages}
                />
                <Separator />

                {/* Details */}
                <DetailsSection
                  formData={formData}
                  setField={setField}
                  transmitter={transmitter}
                />
              </TabsContent>

              {canViewDiagnostics && (
                <TabsContent value="diagnostics">
                  {transmitter && (
                    <DiagnosticsTab
                      transmitter={transmitter}
                      onRefresh={() => populateQuery.refetch()}
                    />
                  )}
                </TabsContent>
              )}

              <TabsContent value="history">
                {transmitter && (
                  <ChangelogTab
                    transmitterId={transmitter._id}
                    enabled={activeTab === 'history'}
                  />
                )}
              </TabsContent>
            </Tabs>
          )}
          </ModalErrorBoundary>
      </EntityFormDialog>

      {/* Delete confirmation */}
      <AlertDialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete transmitter</AlertDialogTitle>
            <AlertDialogDescription>
              Delete transmitter {transmitter?.name || transmitter?.dev_id}? This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              onClick={() => deleteMutation.mutate()}
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Reset SIM confirmation */}
      <AlertDialog open={showResetSimDialog} onOpenChange={setShowResetSimDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Reset SIM connection</AlertDialogTitle>
            <AlertDialogDescription>
              Reset SIM connection for ICCID {transmitter?.ICCID}?
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => {
                if (transmitter?.ICCID) resetSimMutation.mutate(transmitter.ICCID);
              }}
            >
              Reset
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
